import dayjs from "dayjs";
import i18n from "@/i18n/index";
import type { ManipulateType } from "dayjs";
import { jsPDF } from "jspdf";
import autoTable from "jspdf-autotable";

// Extend jsPDF with autoTable
(jsPDF as any).API.autoTable = autoTable;

import { ref } from "vue";
import { useAuthenticatedFetch } from "../auth/useAuthenticatedFetch";
import { Pack, PackRequest } from "@/interfaces/pack";
import { Subscription, SubscriptionRequest } from "@/interfaces/subscription";
import { packs, subscriptions, clubUUID, currentClub } from "@/shared/state";
import { useGlobal } from "@/composables/global/useGlobal";
import { setDefaultImage, getImageUrl } from "@/utils/global";
import {
  createTicket80mm,
  outputTicket80mm,
  TICKET_SPACING_MEDIUM,
} from "@/utils/ticket80mm";

const { authenticatedFetch } = useAuthenticatedFetch();
const API_URL = process.env.VUE_APP_ROOT_API;
export function useCreditPDF() {
  const { formatDate, priceToWords, formatDaysToPeriod, translateToFrench } =
    useGlobal();

  const error = ref<string | null>(null);

  const exportCreditExcel = (data: any, typeName: string) => {
    console.log("hello excel");
    /*
    |--------------------------------------------------------------------------
    | Import XLSX
    |--------------------------------------------------------------------------
    */

    const XLSX = require("xlsx");

    /*
    |--------------------------------------------------------------------------
    | Workbook
    |--------------------------------------------------------------------------
    */

    const workbook = XLSX.utils.book_new();

    /*
    |--------------------------------------------------------------------------
    | Helpers
    |--------------------------------------------------------------------------
    */

    const currency = (value: any): string => {
      return (
        Number(value ?? 0).toFixed(2) +
        " " +
        translateToFrench("symbole.currency.mad")
      );
    };

    const valueOrDash = (value: any): string => {
      if (value === null || value === undefined || value === "") {
        return "-";
      }

      return String(value);
    };

    /*
    |--------------------------------------------------------------------------
    | Member Information
    |--------------------------------------------------------------------------
    */

    const memberInfo: any[][] = [
      [
        translateToFrench("tables.members.matricule"),
        translateToFrench("tables.members.contractNumber"),
        translateToFrench("tables.members.name"),
        translateToFrench("tables.members.cin"),
      ],

      [
        valueOrDash(data?.member_info?.matricule),

        valueOrDash(data?.member_info?.contractNumber),

        valueOrDash(data?.member_info?.name),

        valueOrDash(data?.member_info?.cin),
      ],
    ];

    /*
    |--------------------------------------------------------------------------
    | Subscription Summary
    |--------------------------------------------------------------------------
    */

    const summarySubscription: any[][] = [
      [
        translateToFrench("tables.regulations.net_total"),

        translateToFrench("tables.regulations.total_paid"),

        translateToFrench("tables.regulations.remaining_amount"),
      ],

      [
        currency(data?.summary_subscriptions?.total_amount),

        currency(data?.summary_subscriptions?.total_amount_paid),

        currency(data?.summary_subscriptions?.remaining_amount),
      ],
    ];

    /*
    |--------------------------------------------------------------------------
    | Subscription Headers
    |--------------------------------------------------------------------------
    */

    const headersSubscription = [
      translateToFrench("tables.members.matricule"),

      translateToFrench("tables.members.name"),

      translateToFrench("tables.members.dateBirth"),

      translateToFrench("tables.members.tele"),

      translateToFrench("tables.subscription_member.title"),

      translateToFrench("tables.subscription_member.start_date"),

      translateToFrench("tables.subscription_member.end_date"),

      translateToFrench("tables.subscription_member.remaining_days"),

      translateToFrench("tables.subscription_member.total_amount"),

      translateToFrench("tables.subscription_member.total_amount_paid"),

      translateToFrench("tables.subscription_member.remaining_amount"),
     "Commercial"
    ];

    /*
    |--------------------------------------------------------------------------
    | Subscription Data
    |--------------------------------------------------------------------------
    */

    const subscriptionsMembers: any[][] = [];

    if (
      data?.subscriptions_members &&
      Array.isArray(data.subscriptions_members)
    ) {
      data.subscriptions_members.forEach((item: any) => {
        subscriptionsMembers.push([
          item?.member_info ? valueOrDash(item.member_info?.matricule) : "-",

          item?.member_info ? valueOrDash(item.member_info?.name) : "-",

          item?.member_info?.dateBirth
            ? formatDate(item.member_info.dateBirth)
            : "-",

          item?.member_info ? valueOrDash(item.member_info?.tele) : "-",

          item?.subscription
            ? valueOrDash(item.subscription?.name) +
              " - " +
              valueOrDash(item?.pack?.name)
            : "-",

          item?.start_date ? formatDate(item.start_date) : "-",

          item?.end_date ? formatDate(item.end_date) : "-",

          valueOrDash(item?.status?.remaining_days) +
            " " +
            translateToFrench("symbole.day"),
          currency(item?.total_amount),

          currency(item?.total_amount_paid),

          currency(item?.remaining_amount),
          item?.seller ? valueOrDash(item.seller?.name) : "-",
        ]);
      });
    }

    /*
    |--------------------------------------------------------------------------
    | Filters / Search Information
    |--------------------------------------------------------------------------
    */

    const filterRows: any[][] = [];

    if (data?.filterBy?.memberMatricule) {
      filterRows.push([
        translateToFrench("tables.members.matricule"),

        valueOrDash(data.filterBy.memberMatricule),
      ]);
    }

    if (data?.filterBy?.memberName) {
      filterRows.push([
        translateToFrench("tables.members.name"),

        valueOrDash(data.filterBy.memberName),
      ]);
    }

    if (data?.filterBy?.memberCIN) {
      filterRows.push([
        translateToFrench("tables.members.cin"),

        valueOrDash(data.filterBy.memberCIN),
      ]);
    }

    if (data?.filterBy?.gender) {
      filterRows.push([
        translateToFrench("tables.members.gender"),

        valueOrDash(data.filterBy.gender),
      ]);
    }

    if (
      data?.filterBy?.end_date &&
      data.filterBy.end_date[0] != null &&
      data.filterBy.end_date[1]
    ) {
      const startDate = dayjs(String(data.filterBy.end_date[0])).format(
        "DD/MM/YYYY",
      );

      const endDate = dayjs(String(data.filterBy.end_date[1])).format(
        "DD/MM/YYYY",
      );

      filterRows.push([
        translateToFrench("tables.subscription_member.end_date"),

        `${startDate} -- ${endDate}`,
      ]);
    }

    if (data?.filterBy?.subscription_id) {
      const subscription = subscriptions?.data?.find(
        (subscription: any) =>
          subscription?.id == data.filterBy.subscription_id,
      );

      filterRows.push([
        translateToFrench("tables.subscription_member.subscription_id"),

        valueOrDash(subscription?.name),
      ]);

      if (data?.filterBy?.pack_id != null) {
        const pack = packs?.data?.find(
          (pack: any) => pack?.id == data.filterBy.pack_id,
        );

        filterRows.push([
          translateToFrench("tables.subscription_member.pack_id"),

          valueOrDash(pack?.name),
        ]);
      }
    }

    /*
    |--------------------------------------------------------------------------
    | Sheet 1 - Crédit / Abonnements
    |--------------------------------------------------------------------------
    */

    const sheetData: any[][] = [];

    /*
    | Report title
    */

    sheetData.push([typeName]);

    sheetData.push([]);

    /*
    | Filters
    */

    if (filterRows.length > 0) {
      sheetData.push([translateToFrench("tables.regulations.filters")]);

      filterRows.forEach((row) => {
        sheetData.push(row);
      });

      sheetData.push([]);
    }

    /*
    |--------------------------------------------------------------------------
    | Member Information
    |--------------------------------------------------------------------------
    */

    sheetData.push([
      translateToFrench(
        "views.pdf." + data?.sectionTitle?.toString() + ".member_info_section",
      ),
    ]);

    memberInfo.forEach((row) => {
      sheetData.push(row);
    });

    sheetData.push([]);

    /*
    |--------------------------------------------------------------------------
    | Summary
    |--------------------------------------------------------------------------
    */

    sheetData.push([
      translateToFrench(
        "views.pdf." + data?.sectionTitle?.toString() + ".summary_section",
      ),
    ]);

    summarySubscription.forEach((row) => {
      sheetData.push(row);
    });

    sheetData.push([]);

    /*
    |--------------------------------------------------------------------------
    | Subscriptions
    |--------------------------------------------------------------------------
    */

    sheetData.push([
      translateToFrench(
        "views.pdf." + data?.sectionTitle?.toString() + ".subscription_section",
      ),
    ]);

    sheetData.push(headersSubscription);

    subscriptionsMembers.forEach((row) => {
      sheetData.push(row);
    });

    /*
    |--------------------------------------------------------------------------
    | Create Worksheet
    |--------------------------------------------------------------------------
    */

    const worksheet = XLSX.utils.aoa_to_sheet(sheetData);

    /*
    |--------------------------------------------------------------------------
    | Column Widths
    |--------------------------------------------------------------------------
    */

    worksheet["!cols"] = [
      { wch: 16 },
      { wch: 25 },
      { wch: 15 },
      { wch: 18 },
      { wch: 30 },
      { wch: 16 },
      { wch: 16 },
      { wch: 18 },
      { wch: 20 },
      { wch: 25 },
      { wch: 25 },
    ];

    /*
    |--------------------------------------------------------------------------
    | Freeze Header Area
    |--------------------------------------------------------------------------
    */

    worksheet["!freeze"] = {
      xSplit: 0,
      ySplit: 0,
    };

    /*
    |--------------------------------------------------------------------------
    | Add Worksheet
    |--------------------------------------------------------------------------
    */

    XLSX.utils.book_append_sheet(workbook, worksheet, "Abonnements");

    /*
    |--------------------------------------------------------------------------
    | Create Excel File
    |--------------------------------------------------------------------------
    */

    XLSX.writeFile(workbook, `${typeName}.xlsx`);
  };

  const exportCreditPDF = async (
    data: any,
    typeName: string,
    typeOfFile: string,
  ) => {
    // Create a new jsPDF instance

    const doc = new jsPDF({
      orientation: "landscape",
      unit: "mm",
      format: "a4",
    });

    const headersSubscription = [
      translateToFrench("tables.members.matricule"),
      translateToFrench("tables.members.name"),

      translateToFrench("tables.members.dateBirth"),
      translateToFrench("tables.members.tele"),
      translateToFrench("tables.subscription_member.title"),
      translateToFrench("tables.subscription_member.start_date"),
      translateToFrench("tables.subscription_member.end_date"),
      translateToFrench("tables.subscription_member.remaining_days"),
      translateToFrench("tables.subscription_member.total_amount"),
      translateToFrench("tables.subscription_member.total_amount_paid"),
      translateToFrench("tables.subscription_member.remaining_amount"),
      "Commercail",
    ];

    const subscriptions_members: Array<
      [
        string,
        string,
        string,
        string,
        string,
        string,
        string,
        string,
        string,
        string,
        string,
        string,
      ]
    > = [];
    if (data?.subscriptions_members) {
      data?.subscriptions_members?.map((item, index) => {
        subscriptions_members.push([
          item.member_info
            ? item.member_info?.matricule?.toString() || "-"
            : "-",
          item.member_info ? item.member_info?.name?.toString() || "-" : "-",

          item.member_info
            ? formatDate(item.member_info?.dateBirth?.toString()) || "-"
            : "-",
          item.member_info ? item.member_info?.tele?.toString() || "-" : "-",
          item.subscription
            ? item.subscription?.name?.toString() +
              " - " +
              item?.pack?.name?.toString()
            : "-",
          formatDate(item.start_date),
          formatDate(item.end_date),
          item.status?.remaining_days?.toString() +
            " " +
            translateToFrench("symbole.day"),
          (item?.total_amount?.toFixed(2) ?? "0.00") +
            " " +
            translateToFrench("symbole.currency.mad"),
          (item?.total_amount_paid?.toFixed(2) ?? "0.00") +
            " " +
            translateToFrench("symbole.currency.mad"),
          (item?.remaining_amount?.toFixed(2) ?? "0.00") +
            " " +
            translateToFrench("symbole.currency.mad"),
          item.seller ? item?.seller?.name?.toString() || "-" : "-",
        ]);
      });
    }

    const summarySubscriptionHeader = [
      translateToFrench("tables.regulations.net_total"),
      translateToFrench("tables.regulations.total_paid"),
      translateToFrench("tables.regulations.remaining_amount"),
    ];
    let summarySubscription: Array<Array<string | number>> = [];
    summarySubscription = [
      [
        (data?.summary_subscriptions?.total_amount?.toFixed(2) ?? "0.00") +
          " " +
          translateToFrench("symbole.currency.mad"),
        (data?.summary_subscriptions?.total_amount_paid?.toFixed(2) ?? "0.00") +
          " " +
          translateToFrench("symbole.currency.mad"),
        (data?.summary_subscriptions?.remaining_amount?.toFixed(2) ?? "0.00") +
          " " +
          translateToFrench("symbole.currency.mad"),
      ],
    ];

    const headerMemberInfo = [
      translateToFrench("tables.members.matricule"),
      translateToFrench("tables.members.contractNumber"),
      translateToFrench("tables.members.name"),
      translateToFrench("tables.members.cin"),
    ];
    // const headerMemberInfos = [
    //     translateToFrench('tables.members.tele'),
    //     translateToFrench('tables.members.adresse'),
    //     translateToFrench('tables.members.email'),

    // ];

    let member_info: Array<Array<string | number>> = [];
    let member_infos: Array<Array<string | number>> = [];
    if (data?.member_info) {
      member_info = [
        [
          data?.member_info?.matricule?.toString() ?? "",
          data?.member_info?.contractNumber?.toString() ?? "",
          data?.member_info?.name?.toString() ?? "",
          data?.member_info?.cin?.toString() ?? "",
        ],
      ];
      member_infos = [
        [
          data?.member_info?.tele?.toString() ?? "",
          data?.member_info?.adresse?.toString() ?? "",
          data?.member_info?.email?.toString() ?? "",
        ],
      ];
    }

    // Define articles with varying content types
    const firstArticles: any = [
      {
        type: "article",
        title: translateToFrench(
          "views.pdf." +
            data?.sectionTitle?.toString() +
            ".member_info_section",
        ),
        table: {
          headers: headerMemberInfo,
          data: member_info,
        },
      },
      // {
      //     type: "article",
      //     table: {
      //         headers: headerMemberInfos,
      //         data: member_infos
      //     }
      // },
      {
        type: "article",
        title: translateToFrench(
          "views.pdf." + data?.sectionTitle?.toString() + ".summary_section",
        ),
        table: {
          headers: summarySubscriptionHeader,
          data: summarySubscription,
        },
      },

      {
        type: "article",
        title: translateToFrench(
          "views.pdf." +
            data?.sectionTitle?.toString() +
            ".subscription_section",
        ),
        table: {
          headers: headersSubscription,
          data: subscriptions_members,
        },
      },
    ];

    // Define articles with varying content types
    const articles: any = [];

    // Parameters for positioning

    const pageWidth = doc.internal.pageSize.getWidth(); // Full page width
    const pageHeight = doc.internal.pageSize.getHeight(); // Full page height
    let y = 10;
    const descriptionMarginBottom = 2;
    const descriptionLineHeight = 4;
    const titleMarginBottom = 4;
    const tableMarginBottom = 4;
    const lineHeight = 10;
    const leftX = 10;

    function addPage() {
      if (y + lineHeight + 6 > pageHeight) {
        doc.addPage(); // Create a new page
        y = 10; // Reset y position
      }
    }

    // Helper function to add personal information
    function searchInfo() {
      if (data?.filterBy?.memberMatricule) {
        y += 4.5;
        doc.setFont("Courier", "normal", "normal");
        doc.setFontSize(10);
        doc.text(
          translateToFrench("tables.members.matricule") +
            " : " +
            data?.filterBy?.memberMatricule,
          leftX,
          y,
        );
      }
      if (data?.filterBy?.memberName) {
        y += 4.5;
        doc.setFont("Courier", "normal", "normal");
        doc.setFontSize(10);
        doc.text(
          translateToFrench("tables.members.name") +
            " : " +
            data?.filterBy?.memberName,
          leftX,
          y,
        );
      }
      if (data?.filterBy?.memberCIN) {
        y += 4.5;
        doc.setFont("Courier", "normal", "normal");
        doc.setFontSize(10);
        doc.text(
          translateToFrench("tables.members.cin") +
            " : " +
            data?.filterBy?.memberCIN,
          leftX,
          y,
        );
      }

      if (data?.filterBy?.gender) {
        y += 4.5;
        doc.setFont("Courier", "normal", "normal");
        doc.setFontSize(10);
        doc.text(
          translateToFrench("tables.members.gender") +
            " : " +
            data?.filterBy?.gender,
          leftX,
          y,
        );
      }

      if (data?.filterBy?.end_date) {
        if (
          data?.filterBy?.end_date[0] != null &&
          data?.filterBy?.end_date[1]
        ) {
          y += 4.5;
          const date = data.filterBy?.end_date;
          doc.setFont("Courier", "normal", "normal");
          doc.setFontSize(10);
          const du = dayjs(String(date[0])).format("DD/MM/YYYY");
          const ou = dayjs(String(date[1])).format("DD/MM/YYYY");
          doc.text(
            translateToFrench("tables.subscription_member.end_date") +
              " : " +
              du +
              " -- " +
              ou,
            leftX,
            y,
          );
        }
      }

      if (data?.filterBy?.subscription_id) {
        y += 4.5;
        doc.setFont("Courier", "normal", "normal");
        doc.setFontSize(10);
        const subscription = subscriptions?.data?.find(
          (subscription) => subscription?.id == data?.filterBy?.subscription_id,
        );

        doc.text(
          translateToFrench("tables.subscription_member.subscription_id") +
            " : " +
            (subscription?.name ?? "not found"),
          leftX,
          y,
        );

        if (data?.filterBy?.pack_id != null) {
          y += 4.5;
          doc.setFont("Courier", "normal", "normal");
          doc.setFontSize(10);
          const pack = packs?.data?.find(
            (pack) => pack?.id == data?.filterBy?.pack_id,
          );
          doc.text(
            translateToFrench("tables.subscription_member.pack_id") +
              " : " +
              (pack?.name ?? "not found"),
            leftX,
            y,
          );
        }
      }
    }

    // Helper function to add title and subtitle on the same line
    function addTitleAndSubtitle(article) {
      addPage();
      // Set title font
      doc.setFont("helvetica", "bold");
      doc.setFontSize(9);
      doc.setTextColor(article?.titleColor ?? "#186696"); // Set dynamic color
      doc.text(article?.title, leftX, y);
      // Calculate the width of the title to position the subtitle correctly
      const titleWidth = doc.getTextWidth(article?.title);
      // Set subtitle font and add it next to the title
      if (article?.subtitle) {
        doc.setFont("helvetica", "italic");
        doc.setFontSize(7);
        doc.text(article?.subtitle ?? "", leftX + titleWidth + 5, y); // Add a small space after title
      }
      // Set subtitle font and add it next to the title
      if (article?.priceTitle) {
        doc.setFont("helvetica", "normal", "bold");
        doc.setFontSize(9);
        doc.setTextColor(0, 0, 0); // Set dynamic color
        doc.text(article?.priceTitle ?? "", leftX + titleWidth + 3, y); // Add a small space after title
      }
      y += titleMarginBottom;
    }

    // Helper function to add article description
    function addDescription(article) {
      addPage();
      const font = article?.descriptioBold ? "bold" : "normal";
      const size = article?.descriptioBold ? 8 : 7;
      doc.setFontSize(size);
      doc.setFont("helvetica", "normal", font);
      if (article?.descriptionColor)
        doc.setTextColor(article?.descriptionColor ?? ""); // Set dynamic color
      else doc.setTextColor(0, 0, 0); // Set dynamic color
      const lines = doc.splitTextToSize(
        article?.description ?? "",
        pageWidth - 20,
      ); // Wrap text
      lines.forEach((line) => {
        doc.text(line, leftX, y);
        y += descriptionLineHeight;
      });
      // Calculate the width of the title to position the subtitle correctly
      const titleWidth = doc.getTextWidth(article?.description ?? "");
      // Set subtitle font and add it next to the title
      if (article?.priceDesc) {
        doc.setFont("helvetica", "normal", "bold");
        doc.setFontSize(9);
        doc.text(article?.priceDesc ?? "", leftX + titleWidth + 3, y - 4); // Add a small space after title
      }
      y += descriptionMarginBottom;
    }

    // Helper function to add a bordered table with full-page width
    function addTable(headers, data) {
      if (data) {
        addPage(); // Add a new page if needed
        doc.setFontSize(7);
        doc.setFont("helvetica", "normal");
        doc.setTextColor("#186696"); // Set dynamic color
        const tableY = y; // Initial Y position for the table
        const cellHeight = 5; // Minimum row height
        const tableMarginBottom = 8; // Space after table

        const tableWidth = pageWidth - 20; // Full table width
        const columnWidths = headers.map((header, colIndex) => {
          const headerWidth = doc.getTextWidth(header ?? "") + 4; // Add padding
          const maxDataWidth = data.reduce((max, row) => {
            const cellWidth = doc.getTextWidth(row[colIndex] ?? "") + 4; // Add padding
            return Math.max(max, cellWidth);
          }, 0);
          return Math.max(headerWidth, maxDataWidth); // Choose the larger value
        });
        addPage();
        // Calculate the proportional width for each column
        const totalOriginalWidth = columnWidths.reduce(
          (sum, width) => sum + width,
          0,
        );
        const proportionalWidths = columnWidths.map(
          (width) => (width / totalOriginalWidth) * tableWidth,
        );

        // Draw headers with proportional widths
        let currentX = leftX;
        headers.forEach((header, index) => {
          const cellWidth = proportionalWidths[index];
          doc.rect(currentX, tableY, cellWidth, cellHeight); // Draw border for header cell
          doc.text(header ?? "", currentX + 2, tableY + cellHeight / 2 + 1); // Center text vertically
          currentX += cellWidth;
        });
        addPage();
        y += cellHeight;
        doc.setTextColor("#000"); // Set dynamic color
        // Draw data rows with proportional widths
        data.forEach((row) => {
          currentX = leftX;
          let rowHeight = cellHeight; // Track the height of the current row
          row.forEach((cell, index) => {
            const cellWidth = proportionalWidths[index];
            const wrappedText = doc.splitTextToSize(cell ?? "", cellWidth - 4); // Wrap text to fit cell width
            const textHeight = wrappedText.length * cellHeight; // Calculate height based on wrapped lines
            rowHeight = Math.max(rowHeight, textHeight); // Adjust row height if needed
            addPage();
          });
          addPage(); // Add a new page if needed
          row.forEach((cell, index) => {
            const cellWidth = proportionalWidths[index];
            const wrappedText = doc.splitTextToSize(cell ?? "", cellWidth - 4); // Wrap text to fit cell width
            doc.rect(currentX, y, cellWidth, rowHeight); // Draw border for each cell
            doc.text(wrappedText, currentX + 2, y + cellHeight / 2 + 1); // Draw text
            currentX += cellWidth;
            addPage();
          });
          addPage(); // Add a new page if needed

          y += rowHeight; // Move to the next row
        });

        y += tableMarginBottom; // Extra space after table
      }
    }

    doc.setTextColor(0, 0, 0); // White text
    doc.setFont("helvetica", "normal", "bold");
    doc.setFontSize(14);
    doc.text(typeName, pageWidth / 2, y, { align: "center" });
    y += 6;

    // Add personal information to the PDF
    searchInfo();
    doc.setFont("helvetica", "normal", "normal");
    doc.setFontSize(8);
    y += 4;

    // Loop through articles and render content dynamically
    firstArticles.forEach((article) => {
      if (article?.title && article?.table?.data?.length > 0)
        addTitleAndSubtitle(article);
      if (article?.description && article?.table?.data?.length > 0)
        addDescription(article);
      if (article?.table?.data?.length > 0)
        addTable(article?.table.headers, article?.table.data);
    });

    y += 6;

    // Save or download the doc
    //doc.save(typeName + '.pdf');

    if (typeOfFile == "print") {
      doc.autoPrint();
      window.open(doc.output("bloburl"));
    } else if (typeOfFile == "pdf") {
      doc.save(typeName + ".pdf");
    } else if (typeOfFile == "mail") {
      doc.save(typeName + ".pdf");
    }
  };

  const exportSubscriptionDetailsToPDF = async (
    { logoImageData, logoNaturalWidth, logoNaturalHeight }: any,
    data,
    typeOfFile,
  ) => {
    const ctx = createTicket80mm();
    const {
      doc,
      addDashedLineWithSpacing,
      addTicketHeader,
      addTicketTitle,
      addTicketTable,
      addTicketFooter,
      addNewHeight,
    } = ctx;
    const { pageWidth, marginRight, START_COL_X, X_POS_CENTER } = ctx;
    // Set the desired width and calculate height to maintain the aspect ratio
    const desiredWidth = 22;
    const aspectRatio = logoNaturalHeight / logoNaturalWidth; // Height / Width to maintain aspect ratio
    const desiredHeight = desiredWidth * aspectRatio;
    console.log("logoImageData");
    console.log(logoImageData);
    if (logoImageData) {
      doc.addImage(
        logoImageData,
        "JPEG",
        X_POS_CENTER - desiredWidth / 2,
        ctx.startY,
        desiredWidth,
        desiredHeight,
      );
    }
    ctx.startY += 26;
    await addTicketHeader();
    addTicketTitle(
      translateToFrench("tables.subscription_member.tickets.title") +
        " : " +
        data?.id,
    );

    ctx.startY += TICKET_SPACING_MEDIUM;
    doc.setFont("helvetica", "normal", "bold");
    doc.setFontSize(7);

    doc.text(
      translateToFrench("tables.members.matricule") + " : ",
      START_COL_X,
      ctx.startY,
      { align: "left" },
    );
    doc.text(
      data?.member_info?.matricule?.toString(),
      pageWidth - 5,
      ctx.startY,
      { align: "right" },
    );
    ctx.startY += TICKET_SPACING_MEDIUM;
    doc.text(
      translateToFrench("tables.members.contractNumber") + " : ",
      START_COL_X,
      ctx.startY,
      { align: "left" },
    );
    doc.text(data?.member_info?.contractNumber, pageWidth - 5, ctx.startY, {
      align: "right",
    });
    ctx.startY += TICKET_SPACING_MEDIUM;
    doc.text(
      translateToFrench("tables.members.name") + " : ",
      START_COL_X,
      ctx.startY,
      { align: "left" },
    );
    doc.text(data?.member_info?.name, pageWidth - 5, ctx.startY, {
      align: "right",
    });

    ctx.startY += TICKET_SPACING_MEDIUM;
    doc.text(
      translateToFrench("tables.subscription_member.subscription_id") + " : ",
      START_COL_X,
      ctx.startY,
      { align: "left" },
    );
    doc.text(data?.subscription?.name, pageWidth - 5, ctx.startY, {
      align: "right",
    });

    ctx.startY += TICKET_SPACING_MEDIUM;
    doc.text(
      translateToFrench("tables.subscription_member.pack_id") + " : ",
      START_COL_X,
      ctx.startY,
      { align: "left" },
    );
    doc.text(
      data?.pack?.name +
        ", " +
        data?.pack?.price?.toString() +
        " " +
        translateToFrench("symbole.currency.mad"),
      pageWidth - 5,
      ctx.startY,
      { align: "right" },
    );

    ctx.startY += TICKET_SPACING_MEDIUM;
    doc.text(
      translateToFrench("tables.subscription_member.start_date") + " : ",
      START_COL_X,
      ctx.startY,
      { align: "left" },
    );
    doc.text(formatDate(data?.start_date), pageWidth - 5, ctx.startY, {
      align: "right",
    });
    ctx.startY += TICKET_SPACING_MEDIUM;
    doc.text(
      translateToFrench("tables.subscription_member.end_date") + " : ",
      START_COL_X,
      ctx.startY,
      { align: "left" },
    );
    doc.text(formatDate(data?.end_date), pageWidth - 5, ctx.startY, {
      align: "right",
    });

    ctx.startY += TICKET_SPACING_MEDIUM;
    doc.text(
      translateToFrench("tables.subscription_member.remaining_days") + " : ",
      START_COL_X,
      ctx.startY,
      { align: "left" },
    );
    doc.text(
      data?.status?.remaining_days?.toString() +
        " " +
        translateToFrench("symbole.day"),
      pageWidth - 5,
      ctx.startY,
      { align: "right" },
    );

    if (data?.pack_price) {
      ctx.startY += TICKET_SPACING_MEDIUM;
      doc.text(
        translateToFrench("tables.subscription_member.pack_price") + " ",
        START_COL_X,
        ctx.startY,
        { align: "left" },
      );
      doc.text(
        data?.pack_price?.toFixed(2) +
          " " +
          translateToFrench("symbole.currency.mad"),
        pageWidth - 5,
        ctx.startY,
        { align: "right" },
      );
    }
    if (data?.promo_price) {
      ctx.startY += TICKET_SPACING_MEDIUM;
      doc.text(
        translateToFrench("tables.subscription_member.promo_price") + " ",
        START_COL_X,
        ctx.startY,
        { align: "left" },
      );
      doc.text(
        data?.promo_price?.toFixed(2) +
          " " +
          translateToFrench("symbole.currency.mad"),
        pageWidth - 5,
        ctx.startY,
        { align: "right" },
      );
    }
    if (data?.discount_amount) {
      ctx.startY += TICKET_SPACING_MEDIUM;
      doc.text(
        translateToFrench("tables.subscription_member.remise"),
        START_COL_X,
        ctx.startY,
        { align: "left" },
      );
      doc.text(
        data?.discount_amount?.toFixed(2) +
          " " +
          translateToFrench("symbole.currency.mad"),
        pageWidth - 5,
        ctx.startY,
        { align: "right" },
      );
    }
    if (data?.discounted_price) {
      ctx.startY += TICKET_SPACING_MEDIUM;
      doc.text(
        translateToFrench("tables.subscription_member.discounted_price") + " ",
        START_COL_X,
        ctx.startY,
        { align: "left" },
      );
      doc.text(
        data?.discounted_price?.toFixed(2) +
          " " +
          translateToFrench("symbole.currency.mad"),
        pageWidth - 5,
        ctx.startY,
        { align: "right" },
      );
    }

    ctx.startY += TICKET_SPACING_MEDIUM;
    doc.text(
      translateToFrench("tables.subscription_member.assurance") + " ",
      START_COL_X,
      ctx.startY,
      { align: "left" },
    );
    doc.text(
      (data?.assurance?.cost?.toFixed(2) ?? "0.00") +
        " " +
        translateToFrench("symbole.currency.mad"),
      pageWidth - 5,
      ctx.startY,
      { align: "right" },
    );

    if (data?.total_fees) {
      ctx.startY += TICKET_SPACING_MEDIUM;
      doc.text(
        translateToFrench("tables.subscription_member.fees") + " : ",
        START_COL_X,
        ctx.startY,
        { align: "left" },
      );
      doc.text(
        data?.total_fees?.toFixed(2) +
          " " +
          translateToFrench("symbole.currency.mad"),
        pageWidth - 5,
        ctx.startY,
        { align: "right" },
      );
    }

    addDashedLineWithSpacing();

    if (data?.transactions?.length > 0) {
      const body = data.transactions
        .filter((el: any) => el?.status === "paid")
        .map((el: any) => [
          formatDate(el?.paymentDate),
          el?.paymentMethod ?? "",
          el?.amount_paid?.toFixed(2) +
            " " +
            translateToFrench("symbole.currency.mad"),
        ]);
      addTicketTable({
        head: [
          translateToFrench("tables.payment.paymentDate"),
          translateToFrench("tables.payment.paymentMethod"),
          translateToFrench("tables.payment.amount_paid"),
        ],
        body,
        columnRatios: [0.33, 0.34, 0.33],
      });
    }
    ctx.startY += TICKET_SPACING_MEDIUM;
    addDashedLineWithSpacing();
    addNewHeight();
    ctx.startY += TICKET_SPACING_MEDIUM;

    doc.setFont("helvetica", "normal", "bold");
    doc.setFontSize(8);
    doc.text(
      translateToFrench("tables.regulations.net_total") + " : ",
      START_COL_X,
      ctx.startY,
      { align: "left" },
    );
    doc.text(
      data?.total_amount?.toFixed(2) +
        " " +
        translateToFrench("symbole.currency.mad"),
      pageWidth - marginRight,
      ctx.startY,
      { align: "right" },
    );
    ctx.startY += TICKET_SPACING_MEDIUM;
    addNewHeight();
    doc.text(
      translateToFrench("tables.regulations.total_paid") + " : ",
      START_COL_X,
      ctx.startY,
      { align: "left" },
    );
    doc.text(
      data?.total_amount_paid?.toFixed(2) +
        " " +
        translateToFrench("symbole.currency.mad"),
      pageWidth - marginRight,
      ctx.startY,
      { align: "right" },
    );
    addNewHeight();
    ctx.startY += TICKET_SPACING_MEDIUM;

    doc.text(
      translateToFrench("tables.regulations.remaining_amount") + " : ",
      START_COL_X,
      ctx.startY,
      { align: "left" },
    );

    doc.text(
      data?.remaining_amount?.toFixed(2) +
        " " +
        translateToFrench("symbole.currency.mad"),
      pageWidth - marginRight,
      ctx.startY,
      { align: "right" },
    );

    addDashedLineWithSpacing();
    doc.setFont("helvetica", "normal", "normal");
    doc.setFontSize(7);
    doc.text(priceToWords(data?.total_amount), X_POS_CENTER, ctx.startY, {
      align: "center",
    });

    ctx.startY += TICKET_SPACING_MEDIUM;
    addTicketFooter();

    await outputTicket80mm(doc, {
      typeOfFile,
      fileName:
        "Détails abonnement de " +
        data?.member_info?.name +
        "- Matricule " +
        data?.member_info?.matricule +
        ".pdf",
    });
  };

  const exportPaymentReceipt = async (
    { logoImageData, logoNaturalWidth, logoNaturalHeight }: any,
    currentClub,
    member_subscription,
    paymentReceipt,
    typeOfFile,
  ) => {
    const ctx = createTicket80mm();
    const {
      doc,
      addDashedLineWithSpacing,
      addTicketHeader,
      addTicketTitle,
      addTicketTable,
      addTicketFooter,
      addNewHeight,
    } = ctx;
    const { pageWidth, marginRight, START_COL_X, X_POS_CENTER } = ctx;

    // Set the desired width and calculate height to maintain the aspect ratio
    const desiredWidth = 22;
    const aspectRatio = logoNaturalHeight / logoNaturalWidth; // Height / Width to maintain aspect ratio
    const desiredHeight = desiredWidth * aspectRatio;
    console.log("logoImageData");
    console.log(logoImageData);
    if (logoImageData) {
      doc.addImage(
        logoImageData,
        "JPEG",
        X_POS_CENTER - desiredWidth / 2,
        ctx.startY,
        desiredWidth,
        desiredHeight,
      );
    }
    ctx.startY += 26;
    await addTicketHeader();
    addTicketTitle(
      translateToFrench("tables.stock.sale.transaction_ticket_title") ||
        "Ticket de paiement",
    );

    ctx.startY += TICKET_SPACING_MEDIUM;
    doc.setFont("helvetica", "normal", "bold");
    doc.setFontSize(9);

    doc.text(
      translateToFrench("tables.members.matricule") + " : ",
      START_COL_X,
      ctx.startY,
      { align: "left" },
    );
    doc.text(
      member_subscription?.member_info?.matricule?.toString(),
      pageWidth - 5,
      ctx.startY,
      { align: "right" },
    );

    ctx.startY += TICKET_SPACING_MEDIUM;
    doc.text(
      translateToFrench("tables.members.contractNumber") + " : ",
      START_COL_X,
      ctx.startY,
      { align: "left" },
    );
    doc.text(
      member_subscription?.member_info?.contractNumber,
      pageWidth - 5,
      ctx.startY,
      { align: "right" },
    );

    ctx.startY += TICKET_SPACING_MEDIUM;
    doc.text(
      translateToFrench("tables.members.name") + " : ",
      START_COL_X,
      ctx.startY,
      { align: "left" },
    );
    doc.text(
      member_subscription?.member_info?.name,
      pageWidth - 5,
      ctx.startY,
      { align: "right" },
    );

    ctx.startY += TICKET_SPACING_MEDIUM;
    doc.text(
      translateToFrench("tables.subscription_member.subscription_id") + " : ",
      START_COL_X,
      ctx.startY,
      { align: "left" },
    );
    doc.text(
      member_subscription?.subscription?.name,
      pageWidth - 5,
      ctx.startY,
      { align: "right" },
    );

    ctx.startY += TICKET_SPACING_MEDIUM;
    doc.text(
      translateToFrench("tables.subscription_member.pack_id") + " : ",
      START_COL_X,
      ctx.startY,
      { align: "left" },
    );
    doc.text(
      member_subscription?.pack?.name +
        " " +
        translateToFrench("symbole.currency.mad"),
      pageWidth - 5,
      ctx.startY,
      { align: "right" },
    );

    // startY += 5;
    // doc.text(translateToFrench('tables.subscription_member.activities') + " : ", START_COL_X, startY, { align: "left" });
    // doc.text(
    //     member_subscription?.pack?.name + ", " + member_subscription?.pack?.activitiesName?.toString(),
    //     pageWidth - 5,
    //     startY,
    //     { align: "right" }
    // );

    ctx.startY += TICKET_SPACING_MEDIUM;
    doc.text(
      translateToFrench("tables.subscription_member.start_date") + " : ",
      START_COL_X,
      ctx.startY,
      { align: "left" },
    );
    doc.text(
      formatDate(member_subscription?.start_date),
      pageWidth - 5,
      ctx.startY,
      { align: "right" },
    );
    ctx.startY += TICKET_SPACING_MEDIUM;
    doc.text(
      translateToFrench("tables.subscription_member.end_date") + " : ",
      START_COL_X,
      ctx.startY,
      { align: "left" },
    );
    doc.text(
      formatDate(member_subscription?.end_date),
      pageWidth - 5,
      ctx.startY,
      { align: "right" },
    );

    ctx.startY += TICKET_SPACING_MEDIUM;
    doc.text(
      translateToFrench("tables.subscription_member.remaining_days") + " : ",
      START_COL_X,
      ctx.startY,
      { align: "left" },
    );
    doc.text(
      member_subscription?.status?.remaining_days?.toString() +
        " " +
        translateToFrench("symbole.day"),
      pageWidth - 5,
      ctx.startY,
      { align: "right" },
    );

    if (member_subscription?.pack_price) {
      ctx.startY += TICKET_SPACING_MEDIUM;
      doc.text(
        translateToFrench("tables.subscription_member.pack_price") + " ",
        START_COL_X,
        ctx.startY,
        { align: "left" },
      );
      doc.text(
        (member_subscription?.pack_price?.toFixed(2) ?? "0.00") +
          " " +
          translateToFrench("symbole.currency.mad"),
        pageWidth - 5,
        ctx.startY,
        { align: "right" },
      );
    }
    if (member_subscription?.promo_price) {
      ctx.startY += TICKET_SPACING_MEDIUM;
      doc.text(
        translateToFrench("tables.subscription_member.promo_price") + " ",
        START_COL_X,
        ctx.startY,
        { align: "left" },
      );
      doc.text(
        member_subscription?.promo_price?.toFixed(2) +
          " " +
          translateToFrench("symbole.currency.mad"),
        pageWidth - 5,
        ctx.startY,
        { align: "right" },
      );
    }
    if (member_subscription?.discount_amount) {
      ctx.startY += TICKET_SPACING_MEDIUM;
      doc.text(
        translateToFrench("tables.subscription_member.remise")  ,
        START_COL_X,
        ctx.startY,
        { align: "left" },
      );
      doc.text(
        member_subscription?.discount_amount?.toFixed(2) +
          " " +
          translateToFrench("symbole.currency.mad"),
        pageWidth - 5,
        ctx.startY,
        { align: "right" },
      );
    }
    if (member_subscription?.discounted_price) {
      ctx.startY += TICKET_SPACING_MEDIUM;
      doc.text(
        translateToFrench("tables.subscription_member.discounted_price") + " ",
        START_COL_X,
        ctx.startY,
        { align: "left" },
      );
      doc.text(
        member_subscription?.discounted_price?.toFixed(2) +
          " " +
          translateToFrench("symbole.currency.mad"),
        pageWidth - 5,
        ctx.startY,
        { align: "right" },
      );
    }

    ctx.startY += TICKET_SPACING_MEDIUM;
    doc.text(
      translateToFrench("tables.subscription_member.assurance") + " ",
      START_COL_X,
      ctx.startY,
      { align: "left" },
    );
    doc.text(
      (member_subscription?.assurance?.total_amount?.toString() ?? "0.00") +
        " " +
        translateToFrench("symbole.currency.mad"),
      pageWidth - 5,
      ctx.startY,
      { align: "right" },
    );

    if (member_subscription?.total_fees) {
      ctx.startY += TICKET_SPACING_MEDIUM;
      doc.text(
        translateToFrench("tables.subscription_member.fees") + " : ",
        START_COL_X,
        ctx.startY,
        { align: "left" },
      );
      doc.text(
        member_subscription?.total_fees?.toFixed(2) +
          " " +
          translateToFrench("symbole.currency.mad"),
        pageWidth - 5,
        ctx.startY,
        { align: "right" },
      );
    }

    addDashedLineWithSpacing();

    if (paymentReceipt) {
      addTicketTable({
        head: [
          translateToFrench("tables.payment.paymentDate"),
          translateToFrench("tables.payment.paymentMethod"),
          translateToFrench("tables.payment.amount_paid"),
        ],
        body: [
          [
            formatDate(paymentReceipt?.paymentDate),
            paymentReceipt?.paymentMethod ?? "",
            paymentReceipt?.amount_paid?.toFixed(2) +
              " " +
              translateToFrench("symbole.currency.mad"),
          ],
        ],
        columnRatios: [0.33, 0.34, 0.33],
      });
    }

    addDashedLineWithSpacing();

    doc.setFont("helvetica", "normal", "bold");
    doc.setFontSize(9);
    doc.text(
      translateToFrench("tables.regulations.net_total") + " : ",
      START_COL_X,
      ctx.startY,
      { align: "left" },
    );
    doc.text(
      member_subscription?.total_amount?.toFixed(2) +
        " " +
        translateToFrench("symbole.currency.mad"),
      pageWidth - marginRight,
      ctx.startY,
      { align: "right" },
    );
    ctx.startY += TICKET_SPACING_MEDIUM;

    doc.text(
      translateToFrench("tables.regulations.total_paid") + " : ",
      START_COL_X,
      ctx.startY,
      { align: "left" },
    );
    doc.text(
      member_subscription?.total_amount_paid?.toFixed(2) +
        " " +
        translateToFrench("symbole.currency.mad"),
      pageWidth - marginRight,
      ctx.startY,
      { align: "right" },
    );

    ctx.startY += TICKET_SPACING_MEDIUM;

    doc.text(
      translateToFrench("tables.regulations.remaining_amount") + " : ",
      START_COL_X,
      ctx.startY,
      { align: "left" },
    );

    doc.text(
      member_subscription?.remaining_amount?.toFixed(2) +
        " " +
        translateToFrench("symbole.currency.mad"),
      pageWidth - marginRight,
      ctx.startY,
      { align: "right" },
    );

    addDashedLineWithSpacing();
    doc.setFont("helvetica", "normal", "normal");
    doc.setFontSize(8);
    doc.text(
      priceToWords(member_subscription?.total_amount),
      X_POS_CENTER,
      ctx.startY,
      { align: "center" },
    );

    addTicketFooter();

    await outputTicket80mm(doc, {
      typeOfFile,
      fileName:
        "Ticket de paiement de " +
        member_subscription?.member_info?.name +
        "- Matricule " +
        member_subscription?.member_info?.matricule +
        ".pdf",
    });
  };

  return {
    exportCreditPDF,
    exportCreditExcel,
    exportSubscriptionDetailsToPDF,
    exportPaymentReceipt,
  };
}
